import React, { useEffect, useState } from "react"
import { GeoJSON, LayerGroup, useMap } from "react-leaflet"

import RouteHelper, { findPlannedStateColor } from "../../utils/routeHelper"
import { renderRotatedIcon, renderCircleIcon, applyVesselPopup } from "@/utils/vesselHelper"

export default function VesselIcon({
  vessels,
  markedVesselNames,
  recenterToPoint,
  setMarkedVesselNames,
  setOpenDrawer,
  setOpenVesselList,
  setSelectedVessel,
  setSelectedVesselAnchor,
  onMapVesselClick,
}) {
  const map = useMap()

  // Track viewport movement and trigger re-render
  const [viewportTick, setViewportTick] = useState(0)
  useEffect(() => {
    const onMove = () => setViewportTick((t) => t + 1)
    map.on("moveend", onMove)
    map.on("zoomend", onMove)
    return () => {
      map.off("moveend", onMove)
      map.off("zoomend", onMove)
    }
  }, [map])

  // --- Viewport wrapping helpers for vessel points ---
  const getNorm = () => {
    const b = map.getBounds()
    let west = b.getWest()
    let east = b.getEast()
    if (east <= west) east += 360
    return { west, east }
  }

  const adjustLon = (lon, norm) => {
    let x = lon
    x = x + 360 * Math.round((norm.west - x) / 360)
    while (x < norm.west) x += 360
    while (x > norm.east) x -= 360
    return x
  }

  const inWindow = (lon, norm) => lon >= norm.west && lon <= norm.east

  const projectPointsToViewport = (geojson, norm, renderOnlyInViewport = true) => {
    if (!geojson || !geojson.features) return geojson
    const features = geojson.features
      .map((f) => {
        if (!f?.geometry?.coordinates) return null
        const [lon, lat] = f.geometry.coordinates
        const adjLon = adjustLon(lon, norm)
        if (renderOnlyInViewport && !inWindow(adjLon, norm)) return null
        return {
          ...f,
          geometry: { ...f.geometry, coordinates: [adjLon, lat] },
        }
      })
      .filter(Boolean)
    return { ...geojson, features }
  }

  // Fresh bounds each render — memoised norm was stale after a programmatic recenter.
  const norm = getNorm()

  const handleMarkerClick = (vessel, anchorLatLng) => {
    onMapVesselClick?.(vessel?.vesselName, vessel?.vesselId)
    setSelectedVessel(vessel)
    if (anchorLatLng?.lat != null && anchorLatLng?.lng != null) {
      setSelectedVesselAnchor?.({
        lat: Number(anchorLatLng.lat),
        lng: Number(anchorLatLng.lng),
      })
    }
  }

  return vessels === null ? null : (
    <>
      <LayerGroup>
        {vessels.map((vessel, idx) => {
          let vesselPoints = vessel.points
          const wrapped = projectPointsToViewport(vesselPoints.geojson, norm, true)
          const firstFeature = wrapped?.features?.[0]
          const firstCoords = firstFeature?.geometry?.coordinates ?? []
          const firstTs = firstFeature?.properties?.timestamp ?? "na"
          const featureSignature = `${firstTs}:${firstCoords?.[0] ?? "na"},${firstCoords?.[1] ?? "na"}`
          const vesselColor = RouteHelper.isPlanType(vesselPoints.routeType)
            ? findPlannedStateColor(vesselPoints.state)
            : RouteHelper.findRouteColor(vesselPoints.routeType)
          return wrapped.features === null || wrapped.features.length === null ? null : (
            <GeoJSON
              key={`Vessel-${vessel.points?.vesselName || "unknown"}-${vesselPoints.state}-${vesselPoints.label}-${idx}-${viewportTick}-${wrapped.features?.length || 0}-${featureSignature}`}
              data={wrapped}
              style={{
                fillColor: vesselColor,
                color: vesselColor,
                weight: 1,
              }}
              pointToLayer={(feature, latlon) => {
                // if no analyse available or low distance render CircleIcon
                if (!feature.properties.analyse || feature.properties.analyse.is_low_distance) {
                  return renderCircleIcon({
                    latlon,
                    vesselPoints,
                    recenterToPoint,
                    setMarkedVesselNames,
                    setOpenDrawer,
                    setOpenVesselList,
                    map,
                    handleMarkerClick: (anchorLatLng) =>
                      handleMarkerClick(vesselPoints, anchorLatLng),
                  })
                } else {
                  return renderRotatedIcon({
                    vesselPoints,
                    markedVesselNames,
                    recenterToPoint,
                    setMarkedVesselNames,
                    setOpenDrawer,
                    setOpenVesselList,
                    map,
                    handleMarkerClick: (anchorLatLng) =>
                      handleMarkerClick(vesselPoints, anchorLatLng),
                  })
                }
              }}
              onEachFeature={(feature, layer) => applyVesselPopup({ vesselPoints, feature, layer })}
            />
          )
        })}
      </LayerGroup>
    </>
  )
}
